import { generateHash as generateHashUtility } from '../../react/utils/hash';
import { isDefined } from '../variables';

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
export const escapeRegExp = (text: string) => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

/**
 * Strips away any HTML and decodes the text content.
 *
 * Example:
 * "&#169; Hello <a src="someurl" target="_blank">world</a>" -> © Hello world
 */
export const extractPlainTextFromHtmlString = (htmlString: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  return tempDiv.textContent ?? tempDiv.innerText ?? '';
};

/**
 * Removes symbols and whitespace from a string and
 * only leaves letters, numbers, hyphen and underscore
 */
export const removeSymbolsAndWhiteSpace = (value?: string) => {
  return value?.replace(/[^0-9a-zA-Z_-]/gim, '') ?? '';
};

/**
 * Normalizes a text string and removes accents from letters.
 */
export const normalizeString = (value: string) => {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '');
};

/**
 * Removes all leading and trailing occurrences of a set of characters specified in an array from the
 * current string. If no characters are specified, Unicode white-space characters are removed instead.
 * @param text Text to remove characters from.
 * @param chars Array of characters to remove.
 */
export function trimChars(text: string, chars: string[]) {
  return trimLeftChars(trimRightChars(text, chars), chars);
}

/**
 * Removes all the leading occurrences of a set of characters specified in an array from the
 * current string. If no characters are specified, Unicode white-space characters are removed instead.
 * @param text Text to remove characters from.
 * @param chars Array of characters to remove.
 */
function trimLeftChars(text: string, chars: string[]) {
  // Trim white spaces if no chars specified.
  if (chars.length === 0) {
    return text.trimLeft();
  }

  // Trim leading characters.
  let index = 0;
  while (index <= text.length - 1) {
    if (!chars.includes(text[index])) {
      break;
    }
    index++;
  }

  return text.substr(index);
}

/**
 * Removes all the trailing occurrences of a set of characters specified in an array from the
 * current string. If no characters are specified, Unicode white-space characters are removed instead.
 * @param text Text to remove characters from.
 * @param chars Array of characters to remove.
 */
function trimRightChars(text: string, chars: string[]) {
  // Trim white spaces if no chars specified.
  if (chars.length === 0) {
    return text.trimRight();
  }

  // Trim trailing characters.
  let index = text.length - 1;
  while (index >= 0) {
    if (!chars.includes(text[index])) {
      break;
    }
    index--;
  }

  return text.substr(0, index + 1);
}

function copyTextToClipboard(text: string) {
  const element = document.createElement('textarea');
  element.value = text;
  document.body.prepend(element);
  element.focus();
  element.select();

  try {
    return document.execCommand('copy');
  } catch (err) {
    return false;
  } finally {
    document.body.removeChild(element);
  }
}

export function toUpperFirst(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * @example
 * Undefined joinDefinedStrings(' ', ['A', undefined, 'B', undefined]) will return 'A B'
 */
export function joinIfDefined(seperator: string, values: (string | undefined)[]): string {
  return values.filter(isDefined).join(seperator);
}

/**
 *  Transforms the source text from 'cameCase' to 'Title Case'.
 */
export const camelCaseToTitleCase = (source: string) => {
  const capitalize = (word: string) => {
    return word.charAt(0).toUpperCase() + word.substring(1);
  };

  const words = source.match(/[A-Za-z][a-z]*/g) || [];

  return words.map(capitalize).join(' ');
};

/**
 * Returns a unique hash based on input string.
 */
export const generateHash = (input: string): string => {
  if (input.length === 0) {
    throw new Error('Value needs to be at least 1 character long.');
  }

  return generateHashUtility(input);
};

export const textUtils = {
  copyTextToClipboard,
};
